import { DataSection } from "components/molecules/Card/CardTypes";
import { ReactNode } from "react";
import usePlansState from "recoilStore/Building/usePlansState";
import { formatMonetaryValue } from "utils/formatting";
import { percentageDifference } from "utils/mathFormulas/mathFormulas.helper";

const sumCarbonReductionMeasures = (plan: CarbonReductionPlan, key: string): number =>
    plan.carbonReductionMeasures?.reduce((acc: number, item: any) => acc + (item[key] ?? 0), 0);

const sumAnnualSummaries = (plan: CarbonReductionPlan, key: string): number =>
    plan.annualSummaries?.reduce((acc: number, item: any) => acc + (item[key] ?? 0), 0);

export const prepareCapitalData = (
    referencePlan: CarbonReductionPlan,
    comparedPlan: CarbonReductionPlan,
    comparedPlanName: string,
    referencePlanName: string,
    comparingToBaseline: boolean
) => {
    // Like for Like
    const likeForLikeRef = sumCarbonReductionMeasures(referencePlan, "likeForLikeCost");
    const likeForLikeComp = sumCarbonReductionMeasures(comparedPlan, "likeForLikeCost");
    const likeForLikeEvolution = percentageDifference(likeForLikeComp, likeForLikeRef);

    // Incremental Cost
    const incrementalRef = sumCarbonReductionMeasures(referencePlan, "incrementalCost");
    const incrementalComp = sumCarbonReductionMeasures(comparedPlan, "incrementalCost");
    const incrementalEvolution = percentageDifference(incrementalComp, incrementalRef);

    // Incentives
    const incentiveRef = sumCarbonReductionMeasures(referencePlan, "totalIncentives");
    const incentiveComp = sumCarbonReductionMeasures(comparedPlan, "totalIncentives");
    const incentiveEvolution = percentageDifference(incentiveComp, incentiveRef);

    // Utility savings
    const utilitySavingsRef = sumAnnualSummaries(referencePlan, "annualUtilityCostSavings");
    const utilitySavingsComp = sumAnnualSummaries(comparedPlan, "annualUtilityCostSavings");
    const utilitySavingsEvolution = percentageDifference(utilitySavingsComp, utilitySavingsRef);

    // Revenue savings
    const revenueSavingsRef = sumAnnualSummaries(referencePlan, "revenue");
    const revenueSavingsComp = sumAnnualSummaries(comparedPlan, "revenue");
    const revenueSavingsEvolution = percentageDifference(revenueSavingsComp, revenueSavingsRef);

    // Carbon Policy savings
    const carbonPolicySavingsRef = sumAnnualSummaries(referencePlan, "carbonPolicySavings");
    const carbonPolicySavingsComp = sumAnnualSummaries(comparedPlan, "carbonPolicySavings");
    const carbonPolicySavingsEvolution = percentageDifference(carbonPolicySavingsComp, carbonPolicySavingsRef);

    // Total savings
    const totalSavingsRef = sumAnnualSummaries(referencePlan, "netSavings");
    const totalSavingsComp = sumAnnualSummaries(comparedPlan, "netSavings");
    const totalSavingsEvolution = percentageDifference(totalSavingsComp, totalSavingsRef);

    // Net total cost
    const netTotalCostRef = sumCarbonReductionMeasures(referencePlan, "netTotalCost");
    const netTotalCostComp = sumCarbonReductionMeasures(comparedPlan, "netTotalCost");
    const netTotalCostEvolution = percentageDifference(netTotalCostComp, netTotalCostRef);

    // Capex
    const capexRef = likeForLikeRef + incrementalRef - incentiveRef;
    const capexComp = likeForLikeComp + incrementalComp - incentiveComp;
    const capexEvolution = percentageDifference(capexComp, capexRef);

    // Tooltip
    const tooltipCapexPercentage: ReactNode = (
        <>
            {comparedPlanName} net total cost is ${formatMonetaryValue(netTotalCostComp)}
            <br />
            <br />
            {referencePlanName} net total cost is ${formatMonetaryValue(netTotalCostRef)}
            <br />
            <br />
            There is a {netTotalCostEvolution.toFixed(0)}% difference between the two plans
        </>
    );

    const tooltipSavingsPercentage: ReactNode = (
        <>
            {comparedPlanName} savings are ${formatMonetaryValue(totalSavingsComp)}
            <br />
            <br />
            {referencePlanName} savings are ${formatMonetaryValue(totalSavingsRef)}
            <br />
            <br />
            There is a {totalSavingsEvolution.toFixed(0)}% difference between the two plans
        </>
    );

    // Card Data
    const capexData = {
        header: {
            value: "N/A",
            displayInfoKey: "netTotalCost",
            title: "Capital",
        },
        sections: [
            [
                {
                    value: likeForLikeRef,
                    displayInfoKey: "likeForLikeCost",
                    title: "Like-for-like",
                    increaseDecreasePercentage: hideEvolutionWhenBaseline(likeForLikeEvolution),
                    invertedColor: false,
                },
                {
                    value: incrementalRef,
                    displayInfoKey: "incrementalCost",
                    title: "Incremental",
                    valueColor: "neutral",
                    increaseDecreasePercentage: hideEvolutionWhenBaseline(incrementalEvolution),
                    invertedColor: false,
                },
                {
                    value: -incentiveRef,
                    displayInfoKey: "totalIncentives",
                    title: "Incentives",
                    valueColor: "neutral",
                    increaseDecreasePercentage: hideEvolutionWhenBaseline(incentiveEvolution),
                    invertedColor: false,
                },
                {
                    value: capexRef,
                    displayInfoKey: "totalCapex",
                    title: "Total Capex",
                    valueColor: "neutral",
                    bold: true,
                    increaseDecreasePercentage: hideEvolutionWhenBaseline(capexEvolution),
                    invertedColor: false,
                },
            ],
        ],
    };

    const savingsData = {
        sections: [
            [
                {
                    value: utilitySavingsRef,
                    displayInfoKey: "totalUtilityCostSavings",
                    title: "Utility savings",
                    valueColor: "neutral",
                    increaseDecreasePercentage: hideEvolutionWhenBaseline(utilitySavingsEvolution),
                    invertedColor: true,
                },
                {
                    value: revenueSavingsRef,
                    displayInfoKey: "revenue",
                    title: "Revenue savings",
                    valueColor: "neutral",
                    increaseDecreasePercentage: hideEvolutionWhenBaseline(revenueSavingsEvolution),
                    invertedColor: true,
                },
                {
                    value: carbonPolicySavingsRef,
                    displayInfoKey: "totalUtilityCostSavings",
                    title: "Carbon policies savings",
                    valueColor: "neutral",
                    increaseDecreasePercentage: hideEvolutionWhenBaseline(carbonPolicySavingsEvolution),
                    invertedColor: true,
                },
                {
                    value: totalSavingsRef,
                    displayInfoKey: "totalUtilityCostSavings",
                    title: "Total savings",
                    bold: true,
                    valueColor: "neutral",
                    increaseDecreasePercentage: hideEvolutionWhenBaseline(totalSavingsEvolution),
                    invertedColor: true,
                },
            ],
        ],
    };

    const cardHeaderData: DataSection = [
        {
            key: CapitalSections.CAPEX,
            value: netTotalCostRef,
            displayInfoKey: "netTotalCost",
            title: "Net",
            valueColor: "neutral",
            tooltipElement: tooltipCapexPercentage,
            increaseDecreasePercentage: !comparingToBaseline ? netTotalCostEvolution : undefined,
        },
        {
            key: CapitalSections.SAVINGS,
            value: totalSavingsRef,
            displayInfoKey: "totalUtilityCostSavings",
            title: "Net",
            valueColor: "neutral",
            tooltipElement: tooltipSavingsPercentage,
            increaseDecreasePercentage: !comparingToBaseline ? totalSavingsEvolution : undefined,
            invertedColor: true,
        },
    ];

    return {
        capexData,
        savingsData,
        cardHeaderData,
    };
};

const hideEvolutionWhenBaseline = (evolution: number) => {
    const { comparingToBaseline } = usePlansState();
    return !comparingToBaseline ? evolution : undefined;
};

export enum CapitalSections {
    CAPEX = "Capital",
    SAVINGS = "Savings",
}
